import { cx } from "@suse-o11y/class-variance";
import React from "react";
import ReactMarkdown, { type Components as MarkdownComponents } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { z } from "zod";

import { CopyToClipboard } from "../copyToClipboard/CopyToClipboard";

// https://github.com/remarkjs/react-markdown/blob/HEAD/changelog.md#600---2021-04-15
const LINK_NODE_TYPES: Array<string> = ["a", "p"];
const ALL_NODE_TYPES: Array<string> = [
	"br",
	"p",
	"em",
	"strong",
	"hr",
	"blockquote",
	"del",
	"a",
	"button",
	"img",
	"table",
	"thead",
	"tbody",
	"tr",
	"th",
	"td",
	"ol",
	"ul",
	"li",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"pre",
	"code",
	"video",
];

const drawerTypeSchema = z.enum(["logs", "configuration", "lastChange", "status", "serviceToken"]);

export interface DrawerLink {
	type: z.infer<typeof drawerTypeSchema>;
	args: ReadonlyArray<string>;
}

function parseDrawer(link: string): undefined | DrawerLink {
	const hash = link.split("#").at(-1);
	const [maybeDrawerType, args = ""] = decodeURIComponent(hash ?? "").split("|");

	const drawerType = drawerTypeSchema.safeParse(maybeDrawerType);

	if (!drawerType.success) {
		return undefined;
	}

	return {
		type: drawerType.data,
		args: args.split(","),
	};
}

export interface MarkdownProps {
	className?: string;
	text: string;
	onlyLinks?: boolean;
	trimText?: boolean;
	copyCodeToClipboard?: boolean;
	openLinksInNewTab?: boolean;
	/**
	 * Specifies rendering of deep internal links. If not provided, deep links will be rendered as normal links.
	 */
	renderDeepLink?: (path: string, query: string, children: React.ReactNode) => React.ReactNode;
	/**
	 * Will be applied only on drawer links and buttons.
	 */
	renderDrawerTrigger?: (drawerLink: DrawerLink, children: React.ReactNode) => void;
	/**
	 * When true, displays the markdown as an inline element, instead of as a block. This is done by making both
	 * the wrapper div as well as the rendered markdown element inline.
	 */
	displayInline?: boolean;
	/**
	 * When true, disables raw HTML parsing (rehype-raw) to prevent XSS from untrusted content.
	 * Use this for AI-generated or user-submitted markdown where raw HTML is not expected.
	 */
	sanitize?: boolean;
}

export const Markdown: React.FC<MarkdownProps> = React.memo(
	({
		className,
		text,
		onlyLinks = false,
		trimText = false,
		copyCodeToClipboard = false,
		openLinksInNewTab = true,
		renderDeepLink,
		renderDrawerTrigger,
		displayInline = false,
		sanitize = false,
	}) => {
		const components = React.useMemo(() => {
			const result: MarkdownComponents = {
				button: ({ node, children, ...props }) => {
					const maybeDrawer = parseDrawer(props.value as string);

					if (maybeDrawer) {
						return (
							renderDrawerTrigger?.(
								maybeDrawer,
								<button className="button button-primary button-block my-4 w-full" type="button">
									{children}
								</button>,
							) ?? null
						);
					}

					return (
						<button {...props} disabled={true}>
							{children}
						</button>
					);
				},
				a: ({ node, children, ...props }) => {
					const isInternalLink = props.href?.startsWith("/#");

					const maybeDrawer = parseDrawer(props.href!);
					if (maybeDrawer) {
						return (
							renderDrawerTrigger?.(
								maybeDrawer,
								<button className="link link-primary" type="button">
									{children}
								</button>,
							) ?? null
						);
					}

					// render the deep link for internal links
					if (renderDeepLink && isInternalLink) {
						// the first two characters in the generated link are '/#', remove that, the LinkTo will add it
						const fragments = props.href!.substring(2).split("?");

						return renderDeepLink(fragments.at(0) ?? "", fragments.at(1) ?? "", children);
					}

					return (
						// Reason for noreferrer: https://www.jitbit.com/alexblog/256-targetblank---the-most-underestimated-vulnerability-ever/
						<a
							{...props}
							className={cx(
								"not-prose",
								isInternalLink
									? "hocus:text-inherit text-primary-600 no-underline"
									: "text-grey-800 underline hocus:no-underline",
							)}
							rel="nofollow noreferrer noopener"
							target={openLinksInNewTab ? "_blank" : undefined}
						>
							{children}
						</a>
					);
				},
				p: ({ node, children, ...props }) => {
					if (!trimText) {
						return <p {...props}>{children}</p>;
					}

					// trims the text to at most 3 lines if needed
					// https://css-tricks.com/multi-line-truncation-with-pure-css
					return (
						<p
							{...props}
							className="relative mr-[-1em] max-h-[3.6em] overflow-hidden pr-[1em] text-justify leading-[1.2em]"
						>
							<span className="absolute right-0 bottom-0">...</span>
							{children}
							<span className="absolute right-0 mt-[0.2em] h-[1em] w-[1em] bg-white" />
						</p>
					);
				},
				video: ({ src, poster }) => {
					if (src == null) {
						return null;
					}

					const isRemote = src.startsWith("http");

					return (
						<span className="relative block w-full pt-[56.25%]">
							{isRemote ? (
								<iframe
									allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
									allowFullScreen
									className="absolute inset-0"
									frameBorder="0"
									height="100%"
									src={src}
									width="100%"
								/>
							) : (
								<video className="absolute inset-0" controls height="100%" poster={poster} src={src} width="100%" />
							)}
						</span>
					);
				},

				table: ({ node, ...props }) => <table {...props} className="table-auto" />,
				thead: ({ node, ...props }) => <thead {...props} className="bg-grey-200 font-bold" />,
				tbody: ({ node, ...props }) => <tbody {...props} />,
				tr: ({ isHeader, ...props }) => <tr {...props} className="border border-grey-400 border-solid p-2" />,
				th: ({ isHeader, ...props }) => {
					return (
						// @ts-expect-error props have HTMLTableElement instead of HTMLTableHeaderCellElement
						<th
							{...props}
							// comment to force biome to multi-line the attributes
							className="not-prose border border-grey-400 border-solid p-2 text-left"
						/>
					);
				},
				td: ({ isHeader, ...props }) => {
					return (
						// @ts-expect-error props have HTMLTableElement instead of HTMLTableDataCellElement
						<td
							{...props}
							// comment to force biome to multi-line the attributes
							className="not-prose border border-grey-400 border-solid p-2"
						/>
					);
				},
				pre: ({ node, ...props }) => (
					<CopyToClipboard className="mb-3">
						<pre {...props} />
					</CopyToClipboard>
				),
			};

			if (!copyCodeToClipboard) {
				delete result.pre;
			}

			return result;
		}, [copyCodeToClipboard, openLinksInNewTab, trimText, renderDeepLink, renderDrawerTrigger]);

		return (
			<ReactMarkdown
				allowedElements={onlyLinks ? LINK_NODE_TYPES : ALL_NODE_TYPES}
				className={cx(
					"prose prose-pre:block! select-text prose-pre:font-mono! prose-pre:text-grey-800!",

					/**
					 * The trick of this CSS is that both the outer wrapper and the paragraphs are replaced with `display: inline`.
					 * In order to make multiple paragraphs render correctly we therefore render a line break between subsequent paragraphs.
					 */
					displayInline && [
						"contents",
						"[&_p]:inline",
						"[&_p+p]:before:block [&_p+p]:before:h-[9px] [&_p+p]:before:[content:'_']",
					],

					className,
				)}
				components={components}
				rehypePlugins={sanitize ? [] : [rehypeRaw]}
				remarkPlugins={[remarkGfm]}
			>
				{text}
			</ReactMarkdown>
		);
	},
);

Markdown.displayName = "Markdown";
